import type { Imputacion } from "../Imputaciones";
import type { Respuesta } from "@/ts/typings/clases";
import { ImputacionForm, type ImputacionFormData, } from "@/components/ImputacionForm";
import { BaseModal, ModalCloseCross } from "@/components/modals/BaseModal";
import { Loading, QueryError } from "@/components/Loading/Loading";
import { notificacionTipo } from "@/components/Alerts";
import { useQueryClient } from "@tanstack/react-query";
import { ComunicacionAjax } from "@/ts/api/comunicacion";
import { useRef } from "react";
import { useArticuloKit, type ArticuloKit } from "@/hooks/Api/KitsFabricacion";
import style from "../Imputaciones.module.scss";

interface ModalAceptarImputacionProps {
	readonly show: boolean;
	readonly close: () => void;
	readonly imputacion: Imputacion | null;
}
export function ModalAceptarImputacion(props: ModalAceptarImputacionProps) {
	const { data: articulos, error: errorArticulo } = useArticuloKit(props.imputacion?.idArticulo ?? null);

	if (errorArticulo) return <QueryError error={errorArticulo} fallback="Error al cargar los kits" />;

	return <BaseModal show={props.show} close={props.close}>
		<div className="modal-header">
			<h4 className="modal-title">Imputación</h4>
			<ModalCloseCross onClick={props.close} />
		</div>
		{(props.imputacion !== null && articulos) ? <ModalContent imputacion={props.imputacion} articuloKit={articulos} close={props.close} /> : <Loading />}
	</BaseModal>;
}

function ModalContent(props: { readonly imputacion: Imputacion, readonly articuloKit: ArticuloKit, readonly close: () => void; }) {
	const queryClient = useQueryClient();
	const formRef = useRef<{ handleSubmit: () => void; }>(null);

	const onSubmit = async (data: ImputacionFormData) => {
		const success = await confirmarImputacion(props.imputacion.id, data);
		if (success) {
			props.close();
			void queryClient.invalidateQueries({ queryKey: ["getImputaciones"] });
		}
	};
	const onEliminar = async () => {
		const success = await eliminarImputacion(props.imputacion.id);
		if (success) {
			props.close();
			void queryClient.invalidateQueries({ queryKey: ["getImputaciones"] });
		}
	};
	const initialValues: ImputacionFormData = {
		cantidad: props.imputacion.cantidad,
		lote: props.imputacion.lote,
		notas: props.imputacion.notas,
		idSeccion: props.imputacion.idSeccion,
		fecha: (props.imputacion.fecha ? new Date(props.imputacion.fecha * 1000) : new Date()).toISOString().split("T")[0],
		consumos: props.imputacion.consumos.map(consumo => ({
			idComponente: consumo.idComponente,
			refComponente: consumo.refComponente,
			descComponente: consumo.descComponente,
			cantidadPorUnidad: consumo.cantidadPorUnidad,
			cantidadDivisor: consumo.cantidadDivisor,

			lote: consumo.loteArticulo,
			cantidadDefectuosa: consumo.cantidadDefectuosa,
			cantidadDesperdiciada: consumo.cantidadDesperdiciada,
		})),
	};

	return <>
		<div className={`modal-body  ${style.modalCuerpoImputacion}`}>
			<div style={{ marginBottom: 20 }}>
				<h2 style={{ margin: 0 }}>
					<span>Conjunto: </span>
					<span style={{ color: "#666666", fontWeight: "bold" }} >
						{props.imputacion.nombreArticulo}
					</span>
				</h2>
				<span>{new Date(props.imputacion.fecha ?? Date.now()).toLocaleDateString("es-ES")}</span>
			</div>

			<ImputacionForm
				ref={formRef}
				articuloKit={props.articuloKit}
				initialValues={initialValues}
				onSubmit={(data) => void onSubmit(data)}
			/>
		</div>
		<div className="modal-footer" style={{ display: "block" }}>
			<button type="button" className="btnSecondary" style={{ float: "left" }} onClick={props.close}>Cerrar</button>
			<button type="button" className="btnDanger" onClick={() => void onEliminar()}>Eliminar imputación</button>
			<button type="button" className="btnPrimary" onClick={() => formRef.current?.handleSubmit()} style={{ marginLeft: 12 }}>Confirmar imputación</button>
		</div>
	</>;
}




async function eliminarImputacion(idImputacion: number): Promise<boolean> {
	const url = "/api/v1/eliminarImputacion";
	const response = await ComunicacionAjax.sendAjaxRequest<Respuesta>("POST", url, { idImputacion });
	if (response.tipo == "error") {
		notificacionTipo("error", response.message ?? "Error eliminando Imputación");
		return false;
	}

	if (response.tipo == "warning") {
		notificacionTipo("warning", response.message ?? "Imputación eliminada con advertencias");
		return false;
	}
	notificacionTipo("success", response.message ?? "Imputación eliminada correctamente");

	return true;
}

async function confirmarImputacion(idImputacion: number, values: ImputacionFormData): Promise<boolean> {
	const consumos = values.consumos.map(comp => ({
		idArticulo: comp.idComponente,
		loteArticulo: comp.lote,
		faseArticulo: 0,
		refArticulo: comp.refComponente,
		descArticulo: comp.descComponente,
		idUbicacion: "Z00Z00",
		idContenedor: null,
		cantidadUsada: (comp.cantidadPorUnidad / comp.cantidadDivisor) * values.cantidad,
		cantidadDefectuosa: comp.cantidadDefectuosa,
		cantidadDesperdiciada: comp.cantidadDesperdiciada,
	}));

	const parametro = {
		idImputacion: idImputacion,
		cantidadProducida: values.cantidad,
		tsUnico: Math.trunc(new Date(values.fecha).getTime() / 1000),
		notas: values.notas,
		lote: values.lote,
		idSeccion: values.idSeccion,
		consumos: consumos
	};

	const url = "/api/v1/confirmarImputacion";
	const response = await ComunicacionAjax.sendAjaxRequest<{ status: "exito" | "error", message?: string; }>("POST", url, parametro);
	if (response.status == "error") {
		notificacionTipo("error", response.message ?? "Error confirmando Imputación");
		return false;
	}

	notificacionTipo("success", response.message ?? "Imputación confirmada correctamente");
	return true;
}